import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { FormattedHTMLMessage, FormattedMessage, useIntl } from 'react-intl';
import InputElement from 'react-input-mask';
import cx from 'classnames';
import InputHint from 'components/Credit24/Core/Controls/InputHint';
import {
  getAndroidVersion,
  isChrome,
  keyErrorsFieldRequired,
  removeNonDigitCharacters,
} from 'utils/helpers';

const propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
    active: PropTypes.bool,
    submitFailed: PropTypes.bool,
  }).isRequired,
  type: PropTypes.string,
  title: PropTypes.any,
  tooltip: PropTypes.node,
  disabled: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  isTxtCenter: PropTypes.bool,
  isMaskedInput: PropTypes.bool,
  isErrorVisible: PropTypes.bool,
  forcedErrorOnSubmit: PropTypes.bool,
  isTextButton: PropTypes.bool,
  mask: PropTypes.string,
  maskChar: PropTypes.string,
  hintConfig: PropTypes.shape({
    titleKey: PropTypes.string.isRequired,
    hasLetters: PropTypes.bool,
    hasNumbers: PropTypes.bool,
    hasCapitalLetters: PropTypes.bool,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    hasSpecialCharacter: PropTypes.bool,
  }),
  explanationTextKey: PropTypes.string,
  prefix: PropTypes.string,
  useDigitsGrouping: PropTypes.bool,
  defaultPasswordVisibility: PropTypes.oneOf(['visible', 'hidden']),
  autoComplete: PropTypes.string,
  errorValues: PropTypes.object,
  maxCharacters: PropTypes.number,
  allowOnlyAlphanumeric: PropTypes.bool,
};

const defaultProps = {
  type: 'text',
  title: null,
  tooltip: null,
  disabled: false,
  isFullWidth: false,
  isTxtCenter: false,
  isMaskedInput: false,
  isErrorVisible: true,
  forcedErrorOnSubmit: false,
  isTextButton: false,
  mask: '',
  maskChar: '_',
  hintConfig: null,
  explanationTextKey: '',
  prefix: '',
  useDigitsGrouping: false,
  defaultPasswordVisibility: 'hidden',
  autoComplete: 'on',
  errorValues: null,
  maxCharacters: null,
  allowOnlyAlphanumeric: false,
};

const isOldAndroidVersion = getAndroidVersion() && parseInt(getAndroidVersion(), 10) < 5;

const InputField = (props) => {
  const {
    input,
    meta: {
      touched, error, active, submitFailed,
    },
    title,
    tooltip,
    type: propType,
    disabled,
    mask,
    maskChar,
    isFullWidth,
    isTxtCenter,
    isMaskedInput,
    isErrorVisible,
    forcedErrorOnSubmit,
    isTextButton,
    hintConfig,
    explanationTextKey,
    prefix,
    useDigitsGrouping,
    autoComplete,
    errorValues,
    defaultPasswordVisibility,
    maxCharacters,
    allowOnlyAlphanumeric,
  } = props;

  const intl = useIntl();
  const [inputType, setInputType] = useState(
    propType === 'password' && defaultPasswordVisibility === 'visible'
      ? 'text'
      : propType || 'text'
  );

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipLocked, setTooltipLocked] = useState(false);
  const tooltipContainerRef = useRef(null);

  useEffect(() => {
    setInputType(propType || 'text');
  }, [propType]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        tooltipContainerRef.current
        && !tooltipContainerRef.current.contains(e.target)
      ) {
        setTooltipVisible(false);
        setTooltipLocked(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showTooltip = useCallback(() => {
    if (!tooltipLocked) setTooltipVisible(true);
  }, [tooltipLocked]);

  const hideTooltip = useCallback(() => {
    if (!tooltipLocked) setTooltipVisible(false);
  }, [tooltipLocked]);

  const toggleTooltip = useCallback(() => {
    setTooltipVisible((prev) => {
      const newVis = !prev;
      setTooltipLocked(newVis);
      return newVis;
    });
  }, []);

  const getWasPrefilledByChrome = useCallback(() => (
    isChrome()
      && !!document.querySelector(`.input-txt__field[name=${input.name}]:-webkit-autofill`)
  ), [input.name]);

  const getValueLength = useCallback(() => {
    const value = input.value;
    if (inputType === 'number' && value >= 0) {
      return value.toString().length;
    }
    return `${value}`.length;
  }, [input.value, inputType]);

  const togglePasswordInputType = useCallback(() => {
    setInputType((prev) => (prev === 'password' ? 'text' : 'password'));
  }, []);

  const handleCursor = useCallback(
    ({ target }) => {
      const { selectionStart, selectionEnd, value } = target;
      if (selectionStart === selectionEnd) {
        const firstMaskIndex = value.indexOf(maskChar);
        if (firstMaskIndex >= 0 && firstMaskIndex < selectionStart) {
          target.setSelectionRange(firstMaskIndex, firstMaskIndex);
        }
      }
    },
    [maskChar]
  );

  const filterAlphanumeric = (val) => val.replace(/[^a-zA-Z0-9]/g, '');
  const limitCharacters = (val, max) => (max ? val.slice(0, max) : val);

  const handleInputChange = useCallback((e) => {
    let { value } = e.target;

    if (allowOnlyAlphanumeric) {
      value = filterAlphanumeric(value);
    }

    value = limitCharacters(value, maxCharacters);
    input.onChange(value);
  }, [allowOnlyAlphanumeric, maxCharacters, input]);

  const isFocused = active && !disabled;
  const isErrorTriggered = touched
    && error
    && error !== keyErrorsFieldRequired
    && isErrorVisible;
  const isInErrorState = touched && (error || (forcedErrorOnSubmit && submitFailed));

  const inputClasses = cx({
    'input-txt': true,
    'input-txt--width-full': isFullWidth,
    'input-txt--with-hint': hintConfig || isErrorTriggered,
    'input-txt--state-error': isInErrorState,
    'input-txt--state-disabled': disabled,
    'input-txt--state-active':
      getValueLength() || (prefix && isFocused) || (!touched && getWasPrefilledByChrome()),
    'input-txt--state-focused': isFocused,
    'input-txt--prefix': prefix,
  });

  const fieldClasses = cx({ 'input-txt__field': true, 'txt-center': isTxtCenter });
  const toggleIconClasses = cx({
    'input-txt__toggle-icon': true,
    'input-txt__toggle-icon--type-text': isTextButton,
    'input-txt__toggle-icon--shown': propType === 'password' && inputType === 'text',
  });

  let inputValue = input.value;
  if (useDigitsGrouping && inputValue && inputValue !== 0) {
    inputValue = intl.formatNumber(
      removeNonDigitCharacters(inputValue),
      { useGrouping: true, maximumFractionDigits: 0 }
    );
  }

  let InputComponent = (
    <input
      {...input}
      className={fieldClasses}
      type={inputType}
      autoComplete={propType === 'password' ? 'off' : autoComplete}
      value={inputValue}
      onChange={handleInputChange}
      maxLength={maxCharacters}
    />
  );

  if (isMaskedInput && !isOldAndroidVersion) {
    InputComponent = (
      <InputElement
        {...input}
        className={fieldClasses}
        mask={mask}
        maskChar={maskChar}
        onClick={handleCursor}
        autoComplete={autoComplete}
      />
    );
  }

  let key = error;
  if (hintConfig && !isErrorTriggered) key = hintConfig.titleKey;

  const inputHint = hintConfig ? (
    <InputHint
      {...hintConfig}
      value={inputValue}
      titleKey={key}
      isBlock={!isFocused}
    />
  ) : null;

  const inputError = !hintConfig && isErrorTriggered ? (
    <span className={isFocused ? 'input-txt__error' : 'input-message input-message--error'}>
      {errorValues ? (
        <FormattedHTMLMessage id={key} values={errorValues} />
      ) : (
        <FormattedMessage id={key} />
      )}
    </span>
  ) : null;

  return (
    <>
      <label className={inputClasses} data-prefix={prefix}>
        <div className="input-txt__description" ref={tooltipContainerRef}>
          {title && <span className="input-txt__title">{title}</span>}
          {tooltip && (
            <button
              type="button"
              className="toggle-control__tooltip-trigger-input"
              onClick={toggleTooltip}
              onMouseEnter={showTooltip}
              onMouseLeave={hideTooltip}
              aria-label="Show tooltip"
              aria-expanded={tooltipVisible}
            >
              i
            </button>
          )}
          {tooltipVisible && tooltip && (
            <div className="toggle-control__tooltip-box">
              <div
                className="toggle-control__tooltip-text"
                onClick={() => setTooltipLocked(true)}
              >
                {tooltip}
              </div>
            </div>
          )}
        </div>
        {InputComponent}
        {isFocused && inputHint}
        {isFocused && inputError}
        {propType === 'password' && (
          <button
            className={toggleIconClasses}
            onClick={togglePasswordInputType}
            type="button"
            tabIndex="-1"
          >
            {isTextButton && (
              <FormattedMessage
                id={inputType !== 'text' ? 'password.show' : 'password.hide'}
              />
            )}
          </button>
        )}
      </label>
      {!isFocused && isInErrorState && inputHint}
      {!isFocused && isInErrorState && inputError}
      {explanationTextKey && (
        <span className="input-explanation">
          <FormattedMessage id={explanationTextKey} />
        </span>
      )}
    </>
  );
};

InputField.propTypes = propTypes;
InputField.defaultProps = defaultProps;

export default InputField;
